/**
 * CORE-1004 — сколько человек на самом деле посмотрел.
 *
 * До этого трекер завершения копил **максимум достигнутой позиции**: один
 * бросок ползунка в конец закрывал материал целиком. Для бесплатного контента
 * это терпимо, для платной программы — нет: завершение там основание для
 * сертификата (правила зафиксированы в CORE-997).
 *
 * Здесь живёт измерение и только оно: сколько секунд материала реально
 * воспроизведено. Пороги завершения — не тут, у контуров они разные
 * (геймификация — `usePracticeCompletionTracker`, прогресс программы — сервер).
 *
 * Правило накопления: между двумя отчётами засчитывается дельта медиа-времени,
 * обрезанная сверху по реальному времени, прошедшему на часах. Из этого
 * следует всё остальное:
 *
 * - просмотр на 1.5× и 2× засчитывается честно — часы идут медленнее плёнки, но
 *   запас {@link MAX_PLAYBACK_RATE} это покрывает;
 * - перемотка вперёд бесполезна: за мгновение на часах положено мгновение
 *   просмотра, а не проскочённые полчаса;
 * - перемотка назад ничего не отнимает — счётчик монотонный.
 */

/**
 * Во сколько раз плёнка может опережать часы. Плеер даёт до 2×; четыре — запас
 * на дрожание таймеров и на то, что `timeupdate` приходит не идеально ровно.
 * Именно этот множитель и обесценивает перемотку, поэтому он не «побольше на
 * всякий случай»: на 4× минута реального времени стоит четыре минуты материала.
 */
export const MAX_PLAYBACK_RATE = 4;

/**
 * Насколько длинный разрыв между отчётами ещё считается непрерывным просмотром.
 *
 * `timeupdate` во время проигрывания приходит несколько раз в секунду, так что
 * пять секунд — это уже пауза, свёрнутая вкладка или уснувший ноутбук. Без
 * этого потолка достаточно поставить на паузу на полчаса, вернуться и
 * перемотать на полчаса вперёд: часы-то прошли.
 */
export const MAX_REPORT_GAP_SECONDS = 5;

/**
 * Какую долю материала надо накопить, чтобы событие `ended` считалось
 * досмотром. Без этой проверки «перемотал в конец + ended» даёт ровно тот же
 * обход, ради которого всё и переписано.
 */
export const ENDED_MIN_RATIO = 0.5;

/** Накопленное состояние просмотра одного материала. */
export interface WatchAccumulator {
  /** Фактически воспроизведено, секунды. Только растёт. */
  watchedSeconds: number;
  /**
   * Самая дальняя достигнутая позиция. К просмотру отношения не имеет и на
   * завершение не влияет — нужна как последний источник длительности, когда
   * ни база, ни плеер её не знают.
   */
  maxPositionSeconds: number;
  /** Позиция и время предыдущего отчёта; null — отчётов ещё не было. */
  lastPositionSeconds: number | null;
  lastAtMs: number | null;
}

/** Отчёт плеера: где он сейчас и когда об этом сказал. */
export interface WatchSample {
  positionSeconds: number;
  /** `Date.now()` на момент отчёта. */
  atMs: number;
}

/** Новое состояние и сколько секунд просмотра зачтено этим отчётом. */
export interface WatchStep {
  state: WatchAccumulator;
  creditedSeconds: number;
}

export function emptyWatch(): WatchAccumulator {
  return { watchedSeconds: 0, maxPositionSeconds: 0, lastPositionSeconds: null, lastAtMs: null };
}

/**
 * Учесть отчёт плеера.
 *
 * Возвращает и накопленную сумму, и дельту этого шага: сумма нужна контуру,
 * который считает порог сам, дельта — тому, кто отправляет прогресс на сервер
 * (абсолют ломается, когда человек смотрит с двух устройств).
 */
export function accumulateWatch(state: WatchAccumulator, sample: WatchSample): WatchStep {
  const { positionSeconds, atMs } = sample;

  // Мусорный отчёт не двигает даже якорь: иначе следующая дельта посчитается
  // от несуществующей точки.
  if (!Number.isFinite(positionSeconds) || positionSeconds < 0 || !Number.isFinite(atMs)) {
    return { state, creditedSeconds: 0 };
  }

  const maxPositionSeconds = Math.max(state.maxPositionSeconds, positionSeconds);
  const anchored: WatchAccumulator = {
    ...state,
    maxPositionSeconds,
    lastPositionSeconds: positionSeconds,
    lastAtMs: atMs,
  };

  if (state.lastPositionSeconds == null || state.lastAtMs == null) {
    return { state: anchored, creditedSeconds: 0 };
  }

  const mediaDelta = positionSeconds - state.lastPositionSeconds;
  const wallDelta = (atMs - state.lastAtMs) / 1000;

  // Перемотка назад, пауза на месте и часы, шагнувшие назад, — просто новый
  // якорь. Отнимать нечего: счётчик монотонный.
  if (mediaDelta <= 0 || wallDelta <= 0) {
    return { state: anchored, creditedSeconds: 0 };
  }

  const allowance = Math.min(wallDelta, MAX_REPORT_GAP_SECONDS) * MAX_PLAYBACK_RATE;
  const creditedSeconds = Math.min(mediaDelta, allowance);

  return {
    state: { ...anchored, watchedSeconds: state.watchedSeconds + creditedSeconds },
    creditedSeconds,
  };
}

/**
 * Длительность материала в секундах — от плеера, из базы или из пройденного
 * пути, в таком порядке.
 *
 * **CORE-1139 (QA FAIL 03.09.2026): измеренное важнее записанного.** Раньше
 * первыми стояли метаданные, и это давало прямое противоречие в собственных
 * числах: у практики «Тест» файл длится 33 секунды, а `DurationMinutes` — целые
 * минуты, набранные мастером руками, — говорит «1», то есть 60. Человек
 * досмотрел до конца, а аналитика записала `watched_ratio = 0.55`, средний
 * достигнутый progress занизила вдвое, и веха 75 % у такой практики не
 * наступала никогда: 45 секунд у файла на 33 не бывает. Тем же числом мерился
 * и порог завершения — 48 секунд, которых в материале нет.
 *
 * `duration` у плеера — это сам файл. `DurationMinutes` — то, что о файле
 * написали, и оно не умеет выражать секунды в принципе. Поэтому база остаётся
 * запасным источником: она нужна там, где плеер молчит.
 *
 * `DurationMinutes` в базе nullable и на части строк пуст, а у HLS и у записей
 * эфиров плеер отдаёт `Infinity` или `NaN`. На этом уже горел CORE-936:
 * `Infinity` — truthy и проходит проверку `if (!duration)`, поэтому здесь
 * каждый источник проверяется на конечность, а не на истинность. Для эфирного
 * потока это и означает откат к метаданным.
 *
 * Null означает «неизвестна» — и это ответ, с которым вызывающий обязан
 * работать, а не подставлять ноль.
 */
export function effectiveDurationSeconds(
  totalDurationMinutes: number | null | undefined,
  playerDurationSeconds?: number | null,
  reachedSeconds?: number | null,
): number | null {
  if (typeof playerDurationSeconds === 'number' && Number.isFinite(playerDurationSeconds) && playerDurationSeconds > 0) {
    return playerDurationSeconds;
  }
  if (typeof totalDurationMinutes === 'number' && Number.isFinite(totalDurationMinutes) && totalDurationMinutes > 0) {
    return totalDurationMinutes * 60;
  }
  if (typeof reachedSeconds === 'number' && Number.isFinite(reachedSeconds) && reachedSeconds > 0) {
    return reachedSeconds;
  }
  return null;
}

/**
 * Считать ли `ended` досмотром.
 *
 * Событие говорит только то, что плёнка кончилась, — кончилась она просмотром
 * или прыжком в конец, оно не различает. Различает накопленное: доля
 * {@link ENDED_MIN_RATIO} от длительности.
 *
 * Когда длительность неизвестна вовсе, брать за неё нечего и придираться не к
 * чему: `ended` принимается. Практический случай — короткий материал без
 * `DurationMinutes`, у которого плеер тоже молчит; отказывать ему значит не
 * закрывать его никогда.
 */
export function endedCountsAsWatched(
  state: WatchAccumulator,
  durationSeconds: number | null,
): boolean {
  if (durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0) return true;
  return state.watchedSeconds >= durationSeconds * ENDED_MIN_RATIO;
}
